import { useDispatch, useSelector } from 'react-redux';
import ReactGA from 'react-ga4';

// Images
import { FaCheck } from 'react-icons/fa';
import watchPatAndGem from '../../../images/assessment/watchPatAndGem.png';
import { ReactComponent as Lightning } from '../../../images/assessment/lightning.svg';
import { ReactComponent as Information } from '../../../images/assessment/information.svg';
import { ReactComponent as Logo } from '../../../images//logo.svg';
import { ReactComponent as House } from '../../../images//assessment/house.svg';
import { ReactComponent as Cancel } from '../../../images//assessment/cancel.svg';
import ScoreCircle from './ScoreCircle';
import posthog from 'posthog-js';
import { setAssessmentStep } from '../../../features/assessment/assessmentSlice';
import { getAgeFromDateString, getBMI } from '../../../utils/constants';

const riskFactorMap = new Map([
    ['doesSnoreLoudly', 'Snoring'],
    ['isTired', 'Tiredness'],
    ['doesStopBreathing', 'Pauses in breathing'],
    ['doesStopBreathing2', 'Pauses in breathing'],
    ['isMale', 'Male'],
    ['hasHypertension', 'Hypertension'],
]);

function RiskFactor({ text }) {
    return (
        <div className="flex rounded-xl p-2 bg-red-assessment bg-opacity-20 items-center space-x-1">
            <Lightning />
            <p className="text-sm md:text-base">{text}</p>
        </div>
    );
}

function CheckedItem() {
    return (
        <div className="h-6 w-6 md:h-8 md:w-8 mx-auto bg-green-400 rounded-full">
            <FaCheck className="relative absolute-center pointer-events-none w-2 scale-[1.5] md:scale-[2] text-white" />
        </div>
    );
}

function UncheckedItem() {
    return (
        <div className="h-6 w-6 md:h-8 md:w-8 mx-auto bg-[#2C2C2C] rounded-full">
            <Cancel className="relative absolute-center pointer-events-none w-2 scale-[1.5] md:scale-[2] text-[#616161]" />
        </div>
    );
}

function TableRow({ text, firstChecked, secondChecked }) {
    return (
        <>
            <p className="py-1 text-gray">{text}</p>
            {firstChecked ? <CheckedItem /> : <UncheckedItem />}
            <div className="bg-[#34363F] h-full flex items-center">
                {secondChecked ? <CheckedItem /> : <UncheckedItem />}
            </div>
        </>
    );
}

function DividingLine() {
    return (
        <>
            <div className="col-span-2">
                <div className="h-[1px] w-full bg-gray-dark"></div>
            </div>
            <div className="col-span-1 h-full bg-[#34363F]">
                <div className="h-[1px] w-full bg-gray-dark my-1"></div>
            </div>
        </>
    );
}

function Comparison() {
    return (
        <div className="grid grid-cols-[minmax(0,250px)_80px_80px] justify-center items-center md:ml-auto">
            <div className="col-start-2 flex flex-col items-center text-center gap-1">
                <House />
                <p className="text-[#505050] text-xs">At clinic</p>
            </div>
            <div className="bg-[#34363F] h-full rounded-t-xl">
                <Logo className="max-w-[50px] mx-auto" />
            </div>
            <DividingLine />
            <TableRow
                text="Ability to apply your insurance (covered by many plans)"
                firstChecked={true}
                secondChecked={true}
            />
            <DividingLine />
            <TableRow
                text="Complete at home on your own time"
                firstChecked={false}
                secondChecked={true}
            />
            <DividingLine />
            <TableRow text="Get results in 1 week" firstChecked={false} secondChecked={true} />
            <DividingLine />
            <TableRow
                text="No waitlists or crowded clinics"
                firstChecked={false}
                secondChecked={true}
            />
            <DividingLine />
            <TableRow
                text="Full report and prescription"
                firstChecked={false}
                secondChecked={true}
            />
            <DividingLine />
            <TableRow
                text="No referral for treatment needed"
                firstChecked={false}
                secondChecked={true}
            />
        </div>
    );
}

function determineRiskFactors({ answers }) {
    // Find the keys of the questions not answered false
    const questionsAnswered = Object.keys(answers).filter((key) => !!answers[key]);

    const booleanRiskFactors = questionsAnswered
        // Retrieve the string mapping
        .map((key) => riskFactorMap.get(key))
        // Filter out questions not in the map
        .filter((string) => !!string);

    //If their BMI added to their score, add to the risk factors
    if (getBMI(answers.weightLbs, answers.heightInches) > 35) {
        booleanRiskFactors.concat('Overweight');
    }

    //If their age added to their score, add to the risk factors
    if (getAgeFromDateString(answers.dateOfBirth) > 50) {
        booleanRiskFactors.concat('Age');
    }

    return (
        booleanRiskFactors
            // Display at most 2
            .slice(0, 2)
    );
}

function VersionE() {
    const dispatch = useDispatch();
    const { answers } = useSelector((state) => state.assessment);

    const riskFactors = determineRiskFactors({ answers });

    return (
        <>
            <div className="space-y-2 md:grid md:space-y-4 md:grid-cols-[minmax(0,400px)_minmax(0,624px)] md:gap-x-4 max-w-4xl mx-auto mb-10">
                <div className="grid grid-cols-[100px_1fr] gap-x-2 md:col-span-2 items-center">
                    <ScoreCircle />
                    <p className="font-heading font-bold text-4xl md:text-6xl">
                        You may be <span className="text-red-assessment">at risk</span> for sleep
                        apnea
                    </p>
                </div>

                <div className="space-y-2 col-span-2">
                    <p className="text-xs text-gray py-1 md:text-xl">
                        Based on your risk factors like
                    </p>
                    <div className="flex flex-wrap mb-5 gap-2">
                        {riskFactors.map((factor) => (
                            <RiskFactor key={`factor-${factor}`} text={factor} />
                        ))}
                    </div>
                    <p className="md:text-xl">
                        We recommend a sleep study to get diagnosed and treated with your insurance
                    </p>
                </div>

                <div className="space-y-2">
                    <div
                        style={{ '--image-url': `url(${watchPatAndGem})` }}
                        className="bg-[image:radial-gradient(circle_at_center,rgba(0,0,0,0)_20%,#202330_100%),var(--image-url)] bg-center bg-cover min-h-[400px] max-w-[400px] mx-auto flex items-end"></div>
                    <div className="grid grid-cols-2 text-sm gap-x-2 max-w-[400px] mx-auto">
                        <p className="text-[#45E5A2]">GEM Home Sleep Study</p>
                        <p className="ml-2 text-red-assessment">Clinic Sleep Study</p>
                        <p className="text-white font-heading text-4xl">$0-$320</p>
                        <p className="ml-2 text-gray font-heading text-4xl">$1000+</p>
                    </div>
                    {/* <div className="flex gap-x-2 max-w-[400px]">
                        <Information fill="white" />
                        <p>Costs varies by policy 80% users with UHC pay less than $50</p>
                    </div> */}
                </div>
                <Comparison />
                {/* <button className="btn-shell">Email my results</button> */}
            </div>

            <div className="sticky bottom-2 left-0 w-full backdrop-blur-sm p-2 flex justify-center items-center">
                <button
                    className="btn-primary w-full max-w-4xl"
                    onClick={() => {
                        dispatch(setAssessmentStep('create-account'));

                        ReactGA.event({
                            category: 'User',
                            action: 'Assessment Result',
                            label: 'Continue_Result',
                        });

                        ReactGA.event({
                            category: 'User',
                            action: 'Assessment Result',
                            label: 'Continue_NextSteps',
                        });

                        posthog?.capture('Answered a Question', {
                            question: 'Assessment Result',
                            answer: 'Continue to Create Account',
                        });
                    }}>
                    Continue
                </button>
            </div>
        </>
    );
}

export default VersionE;
